import { CollaborationService } from './collaborationService'
import { Variant } from 'types/questionnaire.types'

interface CollaborationConfig {
  websocketUrl: string
  userId: string
  onDocumentUpdate?: (variantName: string, content: string) => void
  onVariantChange?: (variants: Variant[]) => void
  onQuestionnaireRefresh?: () => void
}

/**
 * Global collaboration manager - singleton that ensures only one service instance per project
 */
class CollaborationManager {
  private static instance: CollaborationManager
  private services: Map<string, CollaborationService> = new Map()

  static getInstance(): CollaborationManager {
    if (!CollaborationManager.instance) {
      CollaborationManager.instance = new CollaborationManager()
    }
    return CollaborationManager.instance
  }

  getService(projectId: string, config: CollaborationConfig): CollaborationService {
    // Check if service already exists for this project
    if (this.services.has(projectId)) {
      const existingService = this.services.get(projectId)!

      // Only update callbacks if they're actually provided (not just checking existence)
      if (config.onDocumentUpdate) {
        existingService.setOnDocumentUpdate(config.onDocumentUpdate)
      }
      if (config.onVariantChange) {
        existingService.setOnVariantChange(config.onVariantChange)
      }
      if (config.onQuestionnaireRefresh) {
        existingService.setOnQuestionnaireRefresh(config.onQuestionnaireRefresh)
      }

      return existingService
    }

    // Create new service
    const service = new CollaborationService({
      websocketUrl: config.websocketUrl,
      projectId,
      userId: config.userId
    })

    if (config.onDocumentUpdate) {
      service.setOnDocumentUpdate(config.onDocumentUpdate)
    }

    if (config.onVariantChange) {
      service.setOnVariantChange(config.onVariantChange)
    }

    if (config.onQuestionnaireRefresh) {
      service.setOnQuestionnaireRefresh(config.onQuestionnaireRefresh)
    }

    this.services.set(projectId, service)
    return service
  }

  removeService(projectId: string): void {
    const service = this.services.get(projectId)
    if (service) {
      service.disconnectAll()
      this.services.delete(projectId)
    }
  }

  hasService(projectId: string): boolean {
    return this.services.has(projectId)
  }
}

export const collaborationManager = CollaborationManager.getInstance()